/**
 * Fait : le formulaire de DÉPÔT d'un document — le sélecteur de fichier, la
 *   limite et les formats dits AVANT le choix, le nom choisi affiché, le refus
 *   d'un fichier hors limite SANS RIEN ENVOYER, et l'avancée de l'envoi
 *   annoncée par paliers.
 *
 *   POURQUOI DES PALIERS, et jamais un pourcentage continu : une région vive
 *   rafraîchie soixante fois par seconde rend un lecteur d'écran inutilisable.
 *   Un quart, la moitié, trois quarts — puis « envoi terminé, lecture en
 *   cours », parce que le serveur accuse réception AVANT que la lecture du
 *   document ne commence : dire « c'est fait » à cet instant serait faux.
 *
 * Ne fait pas : il ne liste rien, ne supprime rien, et ne recharge pas la
 *   liste lui-même — il prévient son parent, qui décide. Il ne réécrit aucune
 *   règle de droit : le serveur refuse la lecture seule, et c'est lui qui
 *   tranche.
 */

import { useRef, useState } from 'react';

import { TAILLE_MAXIMALE_FICHIER_OCTETS } from '@aymoai/shared/donnees';

import { Annonce } from '../gabarits/Annonce.tsx';
import { Champ } from '../gabarits/Champ.tsx';
import { deposeUnDocument } from './api-documents.ts';
import {
  EXTENSIONS_ADMISES,
  FORMATS_EN_CLAIR,
  LIMITE_EN_CLAIR,
  formatDuNom,
  tailleLisible,
} from './mots-des-documents.ts';

/**
 * Les paliers annoncés, et rien entre eux. Le dernier n'est PAS « cent pour
 * cent » : il dit ce qui commence, parce que l'envoi fini n'est pas la fin.
 */
const PALIERS = [
  { part: 0.25, mot: 'Envoi : un quart du document est parti.' },
  { part: 0.5, mot: 'Envoi : la moitié du document est partie.' },
  { part: 0.75, mot: 'Envoi : trois quarts du document sont partis.' },
] as const;

const FIN_DE_L_ENVOI = 'Envoi terminé. La lecture du document commence.';

export function DepotDeDocument({
  projetId,
  surDepot,
}: {
  readonly projetId: string;

  /** Appelé quand un document vient d'être accepté par le serveur. */
  surDepot: () => void;
}) {
  const [fichier, setFichier] = useState<File | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [palier, setPalier] = useState('');
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  /*
   * Le rang du dernier palier annoncé. Une RÉFÉRENCE, et non un état : les
   * avancées arrivent par rafales, et deux d'entre elles lisant le même état
   * annonceraient deux fois le même palier.
   */
  const dernierPalier = useRef(-1);

  /** Remonte le sélecteur après un envoi : un `input` de fichier ne se vide pas. */
  const [remise, setRemise] = useState(0);

  /**
   * Ce qui interdit d'envoyer, ou `null`. MESURÉ SUR LE FICHIER CHOISI, avant
   * toute requête : envoyer dix mégaoctets pour s'entendre dire qu'ils sont de
   * trop, c'est faire payer l'attente à la personne qui voit mal le plus.
   */
  function refusDe(choisi: File): string | null {
    if (choisi.size > TAILLE_MAXIMALE_FICHIER_OCTETS) {
      /*
       * L'EXCÈS, ET NON LES DEUX POIDS. La phrase disait « Ce document pèse
       * 10 Mo, et la limite est de 10 Mo » — mesuré le 2026-08-25 par une
       * épreuve indépendante, sur un fichier de 10,03 Mo. Les deux nombres
       * s'arrondissaient au même mot, et le refus se contredisait lui-même :
       * illisible pour qui relit lentement sa phrase. L'écart, lui, ne peut
       * jamais valoir zéro, et il se dit dans l'unité qui le rend visible.
       */
      const excedent = choisi.size - TAILLE_MAXIMALE_FICHIER_OCTETS;

      return (
        `Ce document dépasse de ${tailleLisible(excedent)} la limite de ` +
        `${LIMITE_EN_CLAIR}. Rien n’a été envoyé.`
      );
    }

    if (formatDuNom(choisi.name) === null) {
      return `Ce format n’est pas admis. Formats acceptés : ${FORMATS_EN_CLAIR}.`;
    }

    return null;
  }

  /** Envoie le fichier choisi, et annonce son avancée par paliers. */
  async function depose(): Promise<void> {
    if (fichier === null) {
      setRefus('Choisissez d’abord un document.');
      return;
    }

    const empeche = refusDe(fichier);
    const format = formatDuNom(fichier.name);
    if (empeche !== null || format === null) {
      setRefus(empeche ?? `Ce format n’est pas admis. Formats acceptés : ${FORMATS_EN_CLAIR}.`);
      return;
    }

    setRefus(null);
    setEnvoiEnCours(true);
    dernierPalier.current = -1;
    setPalier('Envoi commencé.');

    const issue = await deposeUnDocument(projetId, fichier, format, (part) => {
      const rang = PALIERS.findLastIndex((etape) => part >= etape.part);
      if (rang > dernierPalier.current) {
        dernierPalier.current = rang;
        setPalier(PALIERS[rang]?.mot ?? '');
      }
    });

    setEnvoiEnCours(false);

    if (!issue.bon) {
      // Une coupure en cours d'envoi rend une PHRASE, jamais un écran figé.
      setPalier('');
      setRefus(issue.erreur.message);
      return;
    }

    setPalier(FIN_DE_L_ENVOI);
    setFichier(null);
    setRemise((tour) => tour + 1);
    surDepot();
  }

  return (
    <form
      data-role="depot-de-document"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        void depose();
      }}
    >
      <Champ
        key={remise}
        libelle="Document à déposer"
        // LA LIMITE ET LES FORMATS SE LISENT AVANT LE CHOIX : le gabarit pose
        // cette aide entre le libellé et le sélecteur, donc elle est lue en
        // premier — et non découverte dans le message de refus.
        aide={`Formats acceptés : ${FORMATS_EN_CLAIR}. Taille maximale : ${LIMITE_EN_CLAIR}.`}
        type="file"
        accept={EXTENSIONS_ADMISES}
        // `exactOptionalPropertyTypes` : la clé n'existe QUE remplie —
        // « pas d'erreur » n'est pas « une erreur qui vaut rien ».
        {...(refus === null ? {} : { erreur: refus })}
        onChange={(evenement) => {
          const choisi = evenement.target.files?.[0] ?? null;
          setFichier(choisi);
          setPalier('');
          setRefus(choisi === null ? null : refusDe(choisi));
        }}
      />

      {fichier !== null && (
        <p data-role="nom-choisi">
          Document choisi : {fichier.name} ({tailleLisible(fichier.size)})
        </p>
      )}

      {/*
       * La région vive existe AVANT le premier palier : un lecteur d'écran
       * n'annonce pas les changements d'une région qui vient d'apparaître.
       */}
      <span data-role="progression-du-depot">
        <Annonce urgence="poli">{palier}</Annonce>
      </span>

      <button
        type="submit"
        className="principal"
        data-role="deposer-le-document"
        disabled={envoiEnCours}
      >
        {envoiEnCours ? 'Envoi en cours…' : 'Déposer le document'}
      </button>
    </form>
  );
}
